import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import SettingsIcon from '@mui/icons-material/Settings';
import { skipToken } from '@reduxjs/toolkit/query';
import { LayoutCompatibilityBadge } from './LayoutCompatibilityBadge';
import { SkeletonLoader } from '../../UI';
import { useGetSlideThumbnailQuery } from '../../../services/slides.service';
import { config } from '../../../config';

export interface SlideItemCompatibilityBadge {
  layoutName: string;
  supportLevel: SlideLayoutSupportIndicator;
  successTooltipTitle?: React.ReactNode;
  warningTooltipTitle?: React.ReactNode;
  errorTooltipTitle?: React.ReactNode;
  tooltipZIndex?: number;
}

export interface SlideCompatibilityInfo {
  layout_name?: string | null;
  layout_in_all_themes?: boolean;
  matched_theme_names?: string[];
  not_matched_theme_names?: string[];
}

export const getSlideCompatibilityBadge = (
  slide: SlideCompatibilityInfo,
): SlideItemCompatibilityBadge | undefined => {
  const isDeckIngest = Boolean(slide.matched_theme_names && slide.not_matched_theme_names);

  if (!isDeckIngest || !slide.layout_name) {
    return undefined;
  }

  return {
    layoutName: slide.layout_name,
    supportLevel: slide.layout_in_all_themes ? 'FULLY_SUPPORTED' : 'NOT_SUPPORTED',
    successTooltipTitle: 'All themes support this layout',
    errorTooltipTitle: slide.not_matched_theme_names?.length ? (
      <div>
        <div>Not supported by:</div>
        {slide.not_matched_theme_names.map((name) => (
          <div key={name}>• {name}</div>
        ))}
      </div>
    ) : undefined,
  };
};

export interface SlideItemProps {
  slideNumber?: number;
  thumbnailBase64?: string;
  thumbnailUrl?: string | null;
  slideTitle?: string;
  compatibilityBadge?: SlideItemCompatibilityBadge;
  isSelected?: boolean;
  hasSchema?: boolean;
  schemaWarning?: boolean;
  needsDecision?: boolean;
  onClick?: () => void;
}

const SlideItemWrapper = styled.div<{ $isSelected?: boolean; $clickable?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 245px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  outline: ${({ $isSelected, theme }) => ($isSelected ? `2px solid ${theme.palette.black}` : 'none')};
  outline-offset: 2px;
  border-radius: ${({ theme }) => theme.radius.default};
`;

const SlideThumbnail = styled.div<{ $needsDecision?: boolean }>`
  width: 245px;
  height: 138px;
  border-radius: ${({ theme }) => theme.radius.default};
  overflow: hidden;
  background-color: ${({ theme }) => theme.palette.grey || '#f5f5f5'};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  box-shadow: ${({ $needsDecision, theme }) =>
    $needsDecision
      ? `0 0 0 2px ${theme.slides.statusBadge.newVersion.color}`
      : '0 0 0 1px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.08)'};
`;

const DecisionIndicator = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: ${({ theme }) => theme.slides.statusBadge.newVersion.color};
  color: white;
  padding: 3px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  line-height: 1;
`;

const SlideTitle = styled.div`
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.3;
  margin-top: 3px;
  color: ${({ theme }) => theme.palette.textLinkColor};
`;

const SlideImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const SlideNumber = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
`;

const SchemaIndicator = styled.div<{ $warning?: boolean }>`
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: ${({ $warning }) => ($warning ? 'rgba(255, 193, 7, 0.9)' : 'rgba(0, 0, 0, 0.7)')};
  color: ${({ $warning }) => ($warning ? '#000' : 'white')};
  width: 24px;
  height: 24px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 14px;
  }
`;

const SlideItemThumbnail: FC<{
  thumbnailUrl: string;
  slideNumber?: number;
  hasSchema?: boolean;
  schemaWarning?: boolean;
  needsDecision?: boolean;
}> = ({ thumbnailUrl, slideNumber, hasSchema, schemaWarning, needsDecision }) => {
  const [pollInterval, setPollInterval] = useState(0);

  const { data, isLoading } = useGetSlideThumbnailQuery(thumbnailUrl ? { url: thumbnailUrl } : skipToken, {
    pollingInterval: pollInterval,
  });

  useEffect(() => {
    setPollInterval(data?.isGenerating ? config.defaultFetchInterval : 0);
  }, [data?.isGenerating]);

  return (
    <SlideThumbnail $needsDecision={needsDecision}>
      {isLoading || data?.isGenerating ? (
        <SkeletonLoader count={1} height={138} width={245} single />
      ) : data?.url ? (
        <>
          <SlideImage src={data.url} alt={`Slide ${slideNumber}`} />
          {slideNumber != null && <SlideNumber>{slideNumber}</SlideNumber>}
          {hasSchema && (
            <SchemaIndicator $warning={schemaWarning}>
              <SettingsIcon />
            </SchemaIndicator>
          )}
        </>
      ) : (
        <SkeletonLoader count={1} height={138} width={245} single />
      )}
    </SlideThumbnail>
  );
};

export const SlideItem: FC<SlideItemProps> = ({
  slideNumber,
  thumbnailBase64,
  thumbnailUrl,
  slideTitle,
  compatibilityBadge,
  isSelected,
  hasSchema,
  schemaWarning,
  needsDecision,
  onClick,
}) => (
  <SlideItemWrapper $isSelected={isSelected} $clickable={!!onClick} onClick={onClick}>
    {compatibilityBadge && (
      <LayoutCompatibilityBadge
        layoutName={compatibilityBadge.layoutName}
        supportLevel={compatibilityBadge.supportLevel}
        successTooltipTitle={compatibilityBadge.successTooltipTitle}
        warningTooltipTitle={compatibilityBadge.warningTooltipTitle}
        errorTooltipTitle={compatibilityBadge.errorTooltipTitle}
        tooltipZIndex={compatibilityBadge.tooltipZIndex}
      />
    )}

    {thumbnailUrl ? (
      <SlideItemThumbnail
        thumbnailUrl={thumbnailUrl}
        slideNumber={slideNumber}
        hasSchema={hasSchema}
        schemaWarning={schemaWarning}
        needsDecision={needsDecision}
      />
    ) : (
      <SlideThumbnail $needsDecision={needsDecision}>
        {thumbnailBase64 ? (
          <>
            <SlideImage src={`data:image/png;base64,${thumbnailBase64}`} alt={`Slide ${slideNumber}`} />
            {slideNumber != null && <SlideNumber>{slideNumber}</SlideNumber>}
            {hasSchema && (
              <SchemaIndicator $warning={schemaWarning}>
                <SettingsIcon />
              </SchemaIndicator>
            )}
          </>
        ) : (
          <SkeletonLoader count={1} height={138} width={245} single />
        )}
      </SlideThumbnail>
    )}

    {slideTitle && <SlideTitle>{slideTitle}</SlideTitle>}
  </SlideItemWrapper>
);
